import React, { useEffect, useState } from 'react';
import AOS from 'aos';

import client from 'apollo';
import history from './utils/history';
import queries from './apollo/queries';
import { Link } from 'react-router-dom';
import Image from "components/Image";
import 'aos/dist/aos.css';

// Router
import Router from './router';
import ServerUnavailable from './components/Errors/ServerUnavailable';

// Intenationalization
import I18nextProviderC from './config/i18n';
import i18next from 'i18next';

import ScrollToTop from 'components/ScrollToTop';
import { StagePanelProvider } from './contexts/StagePanelContext';
import { EvaluationProvider } from './contexts/EvaluationContext';
import { CurrentStudentProvider } from './contexts/CurrentStudentContext';
import { CurrentStageProvider } from './contexts/CurrentStageContext';

// Style
import './style.sass';

import GA from './utils/GoogleAnalytics';
import ipapi from  'ipapi.co';
import constants from './config/constants';
import sessionManager from './utils/session';
import SessionExpiryWarning from './components/SessionExpiryWarning';
import { ROLES } from './utils/roles';
import { getRoleRedirectPath } from './utils/roleRedirect';
import * as Sentry from '@sentry/react';
import { updateSurveyStatus } from './utils/generals';
import { fireSurveyMoment } from './utils/surveyEngine';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { isPasswordRecoveryRoute } from './utils/passwordRecoveryLink';

AOS.init({ duration: 2000, offset: 300, delay: 1000 });

const isMobileDevice = () =>
  /android/i.test(navigator.userAgent) ||
  (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);

const HEALTH_CHECK_TIMEOUT_MS = isMobileDevice() ? 15000 : 8000;
const HEALTH_CHECK_MAX_ATTEMPTS = 3;
const HEALTH_CHECK_RETRY_DELAY_MS = 1500;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const checkHealthOnce = () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);

  const queryPromise = client.query({
    query: queries.checkHealth,
    fetchPolicy: 'network-only',
    context: { fetchOptions: { signal: controller.signal } },
  });

  const timeoutPromise = new Promise((_, reject) => {
    controller.signal.addEventListener('abort', () =>
      reject(new Error('health check timeout'))
    );
  });

  return Promise.race([queryPromise, timeoutPromise]).finally(() =>
    clearTimeout(timeoutId)
  );
};

const getLocation = () => {
  const locale = localStorage.getItem('locale');
  if (!locale) {
    ipapi.location((data) => {
      if (data && data.languages) {
        const languages = data.languages;
        const mainLanguage = languages.split(',');

        if (mainLanguage.length > 0 && mainLanguage[0].includes('es-')) {
          localStorage.setItem('locale', 'es');
        } else {
          localStorage.setItem('locale', 'en');
          window.location.reload();
        }
      }
    });
  }
};

function App() {
  const [health, setHealth] = useState(true);
  const [openAlert, setOpenAlert] = useState(true);
  const isRecoveryRoute = isPasswordRecoveryRoute();

  // getLocation can reload after its IP lookup. A reload after the recovery
  // credential has been scrubbed from the URL would lose the in-memory token.
  if (!isRecoveryRoute) getLocation();

  useEffect(() => {
    const locale = localStorage.getItem('locale');
    if (!locale) {
      const lang = (
        window.navigator.language || navigator.userLanguage
      ).slice(0, 2);
      switch (lang) {
        case 'en':
          localStorage.setItem('locale', 'en');
          break;
        case 'tr':
          localStorage.setItem('locale', 'tr');
          break;
        case 'es':
        default:
          localStorage.setItem('locale', 'es');
          break;
      }
    }
    checkForMobile();
  }, []);

  useEffect(() => {
    serverHealthCheck();
  }, []);

  // The session's single supervisor for this tab. Started here so there is
  // exactly one timer and one set of listeners no matter how many components
  // mount. The cleanup matters as much as the start: without it a fast remount
  // in development leaves the previous manager's timer running.
  //
  // Started unconditionally, and that is the point. Gating this on a token being
  // present meant that arriving at the login screen — where there is none yet —
  // skipped it, and since signing in navigates within the SPA, App never
  // remounted and nothing ever supervised the session. With no token the manager
  // simply schedules nothing; resume() from the route guard wakes it once one
  // exists.
  useEffect(() => {
    if (isRecoveryRoute) {
      sessionManager.stop();
      return undefined;
    }
    sessionManager.resume();
    return () => sessionManager.stop();
  }, []);

  useEffect(() => {
    if (isRecoveryRoute) return;
    // Esperar un poco para que el Router esté completamente montado
    const timer = setTimeout(() => {
      checkUrlParamsAndLogin();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const checkUrlParamsAndLogin = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const jwt = urlParams.get('jwt');
      const email = urlParams.get('email');
      const userId = urlParams.get('userId');

      // Si hay parámetros de autenticación en la URL
      if (jwt && userId) {
        // Guardar en localStorage
        localStorage.setItem(constants.AUTH_TOKEN, jwt);
        localStorage.setItem('user_id', userId);
        localStorage.setItem('logged_date', new Date().toString());

        // Obtener información del usuario para redirigir según el rol
        try {
          const { data } = await client.query({
            query: queries.profileInfo,
            fetchPolicy: 'network-only',
          });

          const { viewerInfo } = data;
          if (viewerInfo) {
            const { id: user_id, locale } = viewerInfo;

            // Usar el idioma guardado en la BD del usuario
            console.log('Login - viewerInfo locale:', locale);
            if (locale) {
              console.log('Login - Setting locale to:', locale);
              localStorage.setItem('locale', locale);
              i18next.changeLanguage(locale);
              console.log('Login - Changed language to:', locale);
            } else {
              console.log('Login - No locale in viewerInfo');
            }

            // Configurar Sentry
            Sentry.setUser({ id: user_id, email: email || '' });

            // Configurar GA
            GA.Event(GA.categories.USER, GA.actions.LOGIN);

            // Resetear cache de Apollo
            await client.resetStore();
            fireSurveyMoment('user_login');

            // Determinar la ruta según el rol
            const redirectPath = getRoleRedirectPath(viewerInfo);

            // Usar window.location.href para forzar una navegación completa
            // Esto asegura que React Router se inicialice correctamente con la nueva ruta
            window.location.href = redirectPath;
          }
        } catch (error) {
          console.error('Error obteniendo información del usuario:', error);
          // Sin perfil no hay rol sobre el cual decidir, así que se vuelve al login.
          window.location.href = '/auth/login';
        }
      }
    } catch (error) {
      console.error('Error verificando parámetros de URL:', error);
    }
  };

  // Cargar idioma del usuario logueado al recargar la página
  useEffect(() => {
    if (isRecoveryRoute) return;
    const token = localStorage.getItem(constants.AUTH_TOKEN);
    const urlParams = new URLSearchParams(window.location.search);
    const jwt = urlParams.get('jwt');

    // Si el usuario está logueado pero no viene de un login (sin parámetros de URL)
    if (token && !jwt) {
      const loadUserLocale = async () => {
        try {
          const { data } = await client.query({
            query: queries.viewerInfo,
            fetchPolicy: 'network-only',
          });

          if (data?.viewerInfo?.locale) {
            const userLocale = data.viewerInfo.locale;
            localStorage.setItem('locale', userLocale);
            i18next.changeLanguage(userLocale);
          }
        } catch (error) {
          console.error('Error cargando idioma del usuario:', error);
        }
      };

      loadUserLocale();
    }
  }, []);

  const checkForMobile = () => {
    if (
      /android/i.test(navigator.userAgent) ||
      (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream)
    ) {
      const pathname = window.location.pathname;
      if (
        pathname.includes('auth') ||
        pathname.includes('course/') ||
        (pathname.includes('courses') &&
          pathname.includes('stages') &&
          !pathname.includes('feedback')) ||
        (pathname.includes('courses') &&
          pathname.includes('stages') &&
          pathname.includes('feedback'))
      ) {
        window.open(`c1do1:/${pathname}`);
      }
    }
  };

  const serverHealthCheck = async () => {
    for (let attempt = 1; attempt <= HEALTH_CHECK_MAX_ATTEMPTS; attempt++) {
      try {
        const {
          data: { health: health },
        } = await checkHealthOnce();
        setHealth(health);
        return;
      } catch (e) {
        if (attempt === HEALTH_CHECK_MAX_ATTEMPTS) {
          setHealth(false);
        } else {
          await delay(HEALTH_CHECK_RETRY_DELAY_MS);
        }
      }
    }
  };

  const alert = () => {

    return (
      <div class="alert-container" id="alert">
        <div class="alert-box">

          <div class="alert-header">
            <div class="alert-icon">!</div>
            <div class="alert-title">AVISO IMPORTANTE</div>
          </div>


          <p class="alert-message">
            Nuestro canal de soporte por Whatsapp ya no está disponible y podría estar siendo utilizado por terceros. Por favor, eviten responder o interactuar con ese número.
            <br />
            <br />
            Para soporte, contáctenos a través del formulario de contacto o al correo gabriele@c1do1.ai
            <br />
            <br />
            Gracias por su comprensión.
          </p>

          <div class="alert-logo">
            <Image
              uri={require("assets/images/c1do1-logo-new.svg").default}
              className="logo-c1"
            />
          </div>


          <div class="alert-buttons">
            <button class="btn close-btn" onClick={() => setOpenAlert(false)}>CERRAR</button>
            <Link to='/contact'><button class="btn form-btn" onClick={() => setOpenAlert(false)} >IR AL FORMULARIO</button></Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <div id='app'>
      {GA.init() && <GA.RouteTracker />}
      <I18nextProviderC>
        <CurrentStudentProvider>
          <CurrentStageProvider>
            <EvaluationProvider>
              <StagePanelProvider>
                {health || isRecoveryRoute ?
                  <>
                    <ScrollToTop />
                    {/* Mounted once, above the router, so the warning survives
                        navigation and cannot be duplicated by a route that
                        happens to render it too. */}
                    <SessionExpiryWarning />
                    <Router history={history} />
                  </>
                  :
                  <ServerUnavailable />
                }
              </StagePanelProvider>
            </EvaluationProvider>
          </CurrentStageProvider>
        </CurrentStudentProvider>
      </I18nextProviderC>

    </div>
    </GoogleOAuthProvider>
  );
}

export default App;
